/**
 * Auto Acquisition dialog.
 *
 * Lets the operator build an ordered queue of SIC codes and choose whether to
 * run them immediately or at a future time. Unlike AcquisitionDialog (which
 * receives codes from the catalogue's selection), this one includes its own
 * code search so the operator can compose a batch from scratch.
 *
 * Submission calls the same POST /acquisitions endpoint as AcquisitionDialog,
 * passing `scheduledFor` when the operator picks "Later". The API creates the
 * runs in 'scheduled' status; the server's background checker promotes them to
 * 'queued' once the chosen time arrives.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { acquisitionApi, sicApi } from '../lib/api.js';
import {
  IconAlert,
  IconCheck,
  IconClock,
  IconClose,
  IconPlay,
  IconPlus,
  IconSearch,
} from './Icons.jsx';

const DEPTHS = [
  { value: 'list', name: 'List only' },
  { value: 'profile', name: 'Profiles' },
  { value: 'financials', name: 'Financials' },
  { value: 'full', name: 'Full' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'dissolved', label: 'Dissolved' },
  { value: 'liquidation', label: 'Liquidation' },
  { value: 'administration', label: 'Administration' },
];

function formatNumber(value) {
  return typeof value === 'number' ? value.toLocaleString() : '—';
}

export function ScheduleDialog({ onClose }) {
  // Ordered list of { code, description } objects the user has added.
  const [codes, setCodes] = useState([]);

  // SIC code search box.
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchAbortRef = useRef(null);
  const dropdownRef = useRef(null);

  // Acquisition settings.
  const [companyStatus, setCompanyStatus] = useState(['active']);
  const [depth, setDepth] = useState('list');

  // Scheduling.
  const [scheduleMode, setScheduleMode] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('22:00');

  // Preview.
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewAbortRef = useRef(null);

  // Submission.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);

  // Debounced SIC code search.
  useEffect(() => {
    const q = search.trim();
    if (!q) {
      setSearchResults([]);
      setShowDropdown(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      searchAbortRef.current?.abort();
      const ctrl = new AbortController();
      searchAbortRef.current = ctrl;
      setIsSearching(true);
      try {
        const data = await sicApi.search(q, { limit: 8, signal: ctrl.signal });
        setSearchResults(data.items ?? []);
        setShowDropdown(true);
      } catch {
        // Ignore - the field stays usable; results just do not appear.
      } finally {
        if (searchAbortRef.current === ctrl) setIsSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [search]);

  // Preview whenever the code list or company status changes.
  const runPreview = useCallback(async () => {
    if (codes.length === 0) {
      setPreview(null);
      return;
    }

    previewAbortRef.current?.abort();
    const ctrl = new AbortController();
    previewAbortRef.current = ctrl;
    setIsPreviewing(true);
    setPreviewError(null);

    try {
      const data = await acquisitionApi.preview(
        { sicCodes: codes.map((c) => c.code), companyStatus },
        { signal: ctrl.signal },
      );
      setPreview(data);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setPreviewError(err.message ?? 'Could not get a preview from Companies House.');
      setPreview(null);
    } finally {
      if (previewAbortRef.current === ctrl) setIsPreviewing(false);
    }
  }, [codes, companyStatus]);

  useEffect(() => {
    const timer = setTimeout(() => void runPreview(), 400);
    return () => clearTimeout(timer);
  }, [runPreview]);

  // Cleanup on unmount.
  useEffect(
    () => () => {
      searchAbortRef.current?.abort();
      previewAbortRef.current?.abort();
    },
    [],
  );

  // Escape key closes the dialog.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSubmitting, onClose]);

  const addCode = (item) => {
    if (!codes.some((c) => c.code === item.code)) {
      setCodes((prev) => [...prev, { code: item.code, description: item.description }]);
    }
    setSearch('');
    setShowDropdown(false);
  };

  const removeCode = (code) => setCodes((prev) => prev.filter((c) => c.code !== code));

  const moveCode = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= codes.length) return;
    setCodes((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const toggleStatus = (value) => {
    setCompanyStatus((current) => {
      const next = current.includes(value)
        ? current.filter((s) => s !== value)
        : [...current, value];
      return next.length === 0 ? ['active'] : next;
    });
  };

  const getScheduledFor = () => {
    if (scheduleMode !== 'later') return null;
    if (!scheduledDate || !scheduledTime) return null;
    const dt = new Date(`${scheduledDate}T${scheduledTime}:00`);
    return isNaN(dt.getTime()) ? null : dt.toISOString();
  };

  const scheduledFor = getScheduledFor();
  const scheduleIsValid = scheduleMode === 'now' || (scheduledFor !== null && new Date(scheduledFor) > new Date());
  const chosen = preview?.totals?.find((row) => row.depth === depth) ?? null;
  const canSubmit = codes.length > 0 && !isSubmitting && scheduleIsValid;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        sicCodes: codes.map((c) => c.code),
        companyStatus,
        enrichmentDepth: depth,
        ...(scheduledFor ? { scheduledFor } : {}),
      };
      const data = await acquisitionApi.create(body);
      setResult(data);
    } catch (err) {
      setSubmitError(err.message ?? 'Could not schedule the acquisition.');
      setIsSubmitting(false);
    }
  };

  // ---- Result confirmation screen ----
  if (result) {
    const wasScheduled = Boolean(result.scheduledFor);
    return (
      <div className="scrim" onClick={onClose} role="presentation">
        <div
          className="dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-done-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="dialog-header">
            <div>
              <h2 className="dialog-title" id="schedule-done-title">
                <IconCheck /> {wasScheduled ? 'Scheduled' : 'Queued'}
              </h2>
              <p className="dialog-description">
                {result.runs.length} run{result.runs.length === 1 ? '' : 's'}{' '}
                {wasScheduled
                  ? `scheduled for ${new Date(result.scheduledFor).toLocaleString()}.`
                  : 'queued. They execute one at a time, in the order below.'}
              </p>
            </div>
          </div>
          <div className="dialog-body">
            <ul className="queued-list">
              {result.runs.map((run, i) => (
                <li key={run.uuid}>
                  <span className="text-dim text-xs">{i + 1}.</span>
                  <span className="mono">{run.sicCode}</span>
                  <span className="text-dim">{run.description}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="dialog-footer">
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Main dialog ----
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div
      className="scrim"
      onClick={isSubmitting ? undefined : () => onClose()}
      role="presentation"
    >
      <div
        className="dialog dialog-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dialog-header">
          <div>
            <h2 className="dialog-title" id="schedule-title">
              Auto Acquisition
            </h2>
            <p className="dialog-description">
              Build a queue of SIC codes in the order you want them acquired. Each run starts
              automatically once the previous one finishes.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm dialog-close"
            onClick={() => onClose()}
            disabled={isSubmitting}
          >
            Close
          </button>
        </div>

        <div className="dialog-body acquire-grid">
          {/* ---- Left column: setup ---- */}
          <section className="acquire-col">
            <h3 className="acquire-heading">Queue</h3>

            {/* SIC code search */}
            <div className="field">
              <span className="field-label">Search and add SIC codes</span>
              <div className="schedule-search-wrap" ref={dropdownRef}>
                <div className="schedule-search-input-row">
                  <IconSearch size={14} />
                  <input
                    type="text"
                    className="input schedule-search-input"
                    placeholder="Code or keyword, e.g. 78200 or staffing…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    aria-label="Search SIC codes to add to queue"
                    aria-autocomplete="list"
                  />
                  {isSearching ? <span className="spinner" /> : null}
                </div>
                {showDropdown && searchResults.length > 0 ? (
                  <div className="schedule-dropdown" role="listbox" aria-label="SIC code search results">
                    {searchResults.map((item) => {
                      const already = codes.some((c) => c.code === item.code);
                      return (
                        <button
                          key={item.code}
                          type="button"
                          className={`schedule-dropdown-item${already ? ' is-added' : ''}`}
                          onMouseDown={() => !already && addCode(item)}
                          role="option"
                          aria-selected={already}
                        >
                          <span className="mono schedule-dropdown-code">{item.code}</span>
                          <span className="schedule-dropdown-desc">{item.description}</span>
                          {already ? (
                            <span className="badge badge-sm">Added</span>
                          ) : (
                            <IconPlus size={12} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Ordered queue list */}
            {codes.length > 0 ? (
              <div className="schedule-queue">
                {codes.map((code, i) => (
                  <div key={code.code} className="schedule-queue-item">
                    <span className="schedule-queue-order">{i + 1}</span>
                    <span className="schedule-queue-info">
                      <span className="mono">{code.code}</span>
                      <span className="schedule-queue-desc">{code.description}</span>
                    </span>
                    <div className="schedule-queue-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        onClick={() => moveCode(i, -1)}
                        disabled={i === 0}
                        aria-label={`Move ${code.code} up`}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        onClick={() => moveCode(i, 1)}
                        disabled={i === codes.length - 1}
                        aria-label={`Move ${code.code} down`}
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        onClick={() => removeCode(code.code)}
                        aria-label={`Remove ${code.code}`}
                        title="Remove"
                      >
                        <IconClose size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-dim">
                Search above to add codes. The order here is the order they run.
              </p>
            )}

            {/* Enrichment depth - compact version */}
            <div className="field">
              <span className="field-label">Enrichment depth</span>
              <div className="schedule-depth-row">
                {DEPTHS.map((option) => (
                  <label
                    key={option.value}
                    className={`schedule-depth-option${depth === option.value ? ' is-active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="schedule-depth"
                      value={option.value}
                      checked={depth === option.value}
                      onChange={() => setDepth(option.value)}
                    />
                    {option.name}
                  </label>
                ))}
              </div>
            </div>

            {/* Company status */}
            <div className="field">
              <span className="field-label">Company status</span>
              <div className="pill-row">
                {STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`pill ${companyStatus.includes(option.value) ? 'is-active' : ''}`}
                    onClick={() => toggleStatus(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Start time */}
            <div className="field">
              <span className="field-label">When to start</span>
              <div className="pill-row" style={{ marginBottom: 'var(--sp-2)' }}>
                <button
                  type="button"
                  className={`pill ${scheduleMode === 'now' ? 'is-active' : ''}`}
                  onClick={() => setScheduleMode('now')}
                >
                  <IconPlay size={12} />
                  Now
                </button>
                <button
                  type="button"
                  className={`pill ${scheduleMode === 'later' ? 'is-active' : ''}`}
                  onClick={() => setScheduleMode('later')}
                >
                  <IconClock size={12} />
                  Later
                </button>
              </div>

              {scheduleMode === 'later' ? (
                <div className="acquire-row">
                  <label className="field">
                    <span className="field-label">Date</span>
                    <input
                      type="date"
                      className="input"
                      value={scheduledDate}
                      min={todayStr}
                      onChange={(e) => setScheduledDate(e.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Time</span>
                    <input
                      type="time"
                      className="input"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                    />
                  </label>
                </div>
              ) : null}

              {scheduleMode === 'later' && !scheduleIsValid ? (
                <p className="text-xs" style={{ color: 'var(--tone-warn)' }}>
                  Choose a date and time in the future.
                </p>
              ) : null}
            </div>
          </section>

          {/* ---- Right column: preview ---- */}
          <section className="acquire-col">
            <h3 className="acquire-heading">
              Preview {isPreviewing ? <span className="spinner" /> : null}
            </h3>

            {previewError ? (
              <div className="alert alert-error">
                <span className="alert-icon">
                  <IconAlert />
                </span>
                <span className="alert-body">{previewError}</span>
              </div>
            ) : codes.length === 0 ? (
              <p className="text-sm text-dim">Add SIC codes on the left to see a company count and time estimate.</p>
            ) : preview ? (
              <>
                <div className="stat-row">
                  <div className="stat">
                    <span className="stat-value">{formatNumber(preview.totalHits)}</span>
                    <span className="stat-label">companies</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{formatNumber(chosen?.apiCalls)}</span>
                    <span className="stat-label">API calls</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{chosen?.durationLabel ?? '—'}</span>
                    <span className="stat-label">estimated total</span>
                  </div>
                </div>

                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Code</th>
                        <th scope="col">Description</th>
                        <th scope="col" className="table-num">
                          Companies
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map((code, i) => {
                        const row = preview.codes?.find((c) => c.sicCode === code.code);
                        return (
                          <tr key={code.code}>
                            <td className="text-dim">{i + 1}</td>
                            <td className="mono">{code.code}</td>
                            <td>{code.description}</td>
                            <td className="table-num">
                              {row?.hits === null ? (
                                <span className="text-dim">unknown</span>
                              ) : (
                                formatNumber(row?.hits)
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {scheduleMode === 'later' && scheduledFor ? (
                  <div className="alert alert-info">
                    <span className="alert-icon">
                      <IconClock />
                    </span>
                    <span className="alert-body">
                      First run will start at{' '}
                      <strong>{new Date(scheduledFor).toLocaleString()}</strong>. Any runs already
                      in the queue at that time will go before these.
                    </span>
                  </div>
                ) : null}

                {preview.rateLimit ? (
                  <p className="text-sm text-dim">
                    Rate limit: <span className="mono">{preview.rateLimit.used}</span> of{' '}
                    <span className="mono">{preview.rateLimit.limit}</span> calls used this window.
                  </p>
                ) : null}
              </>
            ) : (
              !previewError && (
                <p className="text-sm text-dim">Counting companies at Companies House…</p>
              )
            )}
          </section>
        </div>

        {submitError ? (
          <div className="alert alert-error" style={{ margin: '0 var(--sp-6) var(--sp-4)' }}>
            <span className="alert-icon">
              <IconAlert />
            </span>
            <span className="alert-body">{submitError}</span>
          </div>
        ) : null}

        <div className="dialog-footer">
          <button type="button" className="btn btn-ghost" onClick={() => onClose()} disabled={isSubmitting}>
            Cancel
          </button>
          <span className="dialog-footer-spacer" />
          <button
            type="button"
            className="btn"
            onClick={() => void runPreview()}
            disabled={isPreviewing || codes.length === 0}
          >
            Re-count
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {isSubmitting ? (
              <span className="spinner" />
            ) : scheduleMode === 'later' ? (
              <IconClock size={14} />
            ) : (
              <IconPlay />
            )}
            {scheduleMode === 'later'
              ? 'Schedule batch'
              : `Queue ${codes.length > 1 ? `${codes.length} runs` : 'run'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
